<script lang="ts">
import type { HekateDisplayField } from '@onshape/www-inertia';
import CoordinatorProvider from '../coordinator/CoordinatorProvider';
import FieldErrorMessage from './FieldErrorMessage.vue';
import type { SetupContext } from 'vue';

/** Phone input field with country code selector */
export default defineComponent({
  components: {
    FieldErrorMessage,
  },
  props: {
    field: {
      required: true,
      type: Object as unknown as () => HekateDisplayField,
    },
    modelValue: {
      required: true,
      type: String,
    },
  },
  emits: ['blur', 'focus', 'update:modelValue'],
  setup(props: { modelValue: string; field: HekateDisplayField }, context: SetupContext) {
    const { field, modelValue } = toRefs(props);
    return CoordinatorProvider.get().phoneNumberField({
      field,
      modelValue,
      context,
    });
  },
});
</script>

<template>
  <div class="field-phone-number field-container">
    <div
      :class="{
        'field-content': true,
        'field-content-is-validated': field.isValidated,
        'field-content-is-errored': field.errorMessages.length > 0,
      }"
    >
      <label :for="`${field.htmlName}-for-${field.id}`">
        <span v-html="$t(field.name, field.htmlName, field.label)" /><sup
          v-if="field.isRequired"
          class="required-indicator"
          title="This field is required"
          >*</sup
        >
      </label>
      <div class="phone-fields">
        <select
          :id="`${field.htmlName}-for-${field.id}`"
          v-model="countryCode"
          class="country-code-select prefix-dropdown-select"
          :required="field.isRequired"
          :disabled="field.isDisabled"
          @change="conditionallySelectEmit('blur', field)"
          @blur="conditionallySelectEmit('blur', field)"
          @focus="conditionallySelectEmit('focus', field)"
        >
          <option
            class="default-option"
            value=""
            v-text="$t('Select', 'Select')"
          />

          <option
            v-if="yourCountryCodeInfo"
            :value="yourCountryCodeInfo.code"
            name="Country Code"
          >
            {{ yourCountryCodeInfo.twoChar }} (+{{ yourCountryCodeInfo.code }})
          </option>
          <option
            v-if="yourCountryCodeInfo"
            class="spacer"
            disabled
          >
            ──────────
          </option>

          <option
            v-for="info in commonCountryCodeInfos"
            :key="info.twoChar"
            :value="info.code"
            name="Country Code"
          >
            {{ info.twoChar }} (+{{ info.code }})
          </option>
          <option
            class="spacer"
            disabled
          >
            ──────────
          </option>

          <option
            v-for="info in allCountryCodeInfos"
            :key="info.twoChar"
            :value="info.code"
            name="Country Code"
          >
            {{ info.twoChar }} (+{{ info.code }})
          </option>
        </select>

        <input
          v-model="phoneNumber"
          type="tel"
          class="phone-number-input"
          name="Phone Number"
          :required="field.isRequired"
          :disabled="field.isDisabled"
          @blur="$emit('blur', field)"
          @focus="$emit('focus', field)"
        />
      </div>
      <input
        v-model="bubbleModel"
        type="hidden"
        :html-name="field.htmlName"
      />

      <field-error-message :messages="field.errorMessages" />
    </div>
  </div>
</template>

<style lang="scss" scoped>
@use '../../../style/vars.scss' as vars;

#hekate-form-fort-container {
  div.field-phone-number.field-container {
    div.phone-fields {
      display: flex;
      flex-direction: row;
      justify-content: space-between;

      select.country-code-select {
        width: 36%;
        font-size: 0.8rem;
        margin-right: 4px;
        .default-option {
          text-align: center;
        }
      }

      input.phone-number-input {
        width: 75%;
      }
    }
  }
}
</style>
